import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
// import { BusinessUnitEnum } from '../utils/constants';
import StatusChip from '../components/ui/StatusChip';
import { useAuth } from '../contexts/AuthContext';
import './Dashboard.css';

// Glass Card Component
const GlassCard = ({ children, onClick, ...props }) => {
  return (
    <motion.div
      className={`glass-card ${onClick ? 'clickable' : ''}`}
      whileHover={{ y: -5 }}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      onClick={onClick}
      {...props}
    >
      {children}
    </motion.div>
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [winners, setWinners] = useState({});
  const [currentElection, setCurrentElection] = useState(null);
  const [upcomingElection, setUpcomingElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayedWinners, setDisplayedWinners] = useState({});

  useEffect(() => {
    const fetchWinnersAndElection = async () => {
      try {
        // Fetch current winners
        const winnersResponse = await axios.get('/api/votes/winners');
        setWinners(winnersResponse.data);
        
        // Fetch active election from backend
        let current = null;
        try {
          const currentResp = await axios.get('/api/elections/current');
          current = currentResp.data;
        } catch (e) {
          // 404 means no active election
          if (!(e?.response && e.response.status === 404)) {
            throw e;
          }
        }
        setCurrentElection(current);

        // Show all business units (winners API now returns all 5 with vacant status)
        setDisplayedWinners(winnersResponse.data);

        // Fetch all elections to detect nomination period/upcoming status
        try {
          const electionsResponse = await axios.get('/api/elections/');
          const elections = electionsResponse.data;
          const now = new Date();
          const upcoming = elections.find(election => {
            const nominationStart = new Date(election.nomination_start_date);
            const votingStart = new Date(election.voting_start_date);
            return now >= nominationStart && now < votingStart;
          });
          setUpcomingElection(upcoming || null);
        } catch (e) {
          // Non-fatal for dashboard if this fails
          setUpcomingElection(null);
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to fetch data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchWinnersAndElection();
  }, []);
  
  const handleElectionAction = () => {
    if (!currentElection) {
      if (user.role === 'admin') {
        navigate('/admin');
      }
      return;
    }
    
    const now = new Date();
    if (now >= new Date(currentElection.nomination_start_date) && 
        now <= new Date(currentElection.nomination_end_date)) {
      navigate('/nominate');
    } else if (now >= new Date(currentElection.voting_start_date) && 
              now <= new Date(currentElection.voting_end_date)) {
      navigate('/vote');
    } else {
      // Default action: go to vote page to see election info
      navigate('/nominate');
    }
  };

  const getCurrentElectionStatus = () => {
    const now = new Date();
    if (currentElection) {
      const nominationStart = new Date(currentElection.nomination_start_date);
      const nominationEnd = new Date(currentElection.nomination_end_date);
      const votingStart = new Date(currentElection.voting_start_date);
      const votingEnd = new Date(currentElection.voting_end_date);
      if (now >= nominationStart && now <= nominationEnd) {
        return { label: 'Nominations Open', color: 'success' };
      }
      if (now > nominationEnd && now < votingStart) {
        return { label: 'Nominations Closed', color: 'pending' };
      }
      if (now >= votingStart && now <= votingEnd) {
        return { label: 'Voting Open', color: 'primary' };
      }
    }
    if (upcomingElection) {
      const nominationStart = new Date(upcomingElection.nomination_start_date);
      const nominationEnd = new Date(upcomingElection.nomination_end_date);
      const votingStart = new Date(upcomingElection.voting_start_date);
      if (now >= nominationStart && now <= nominationEnd) {
        return { label: 'Nominations Open', color: 'success' };
      }
      if (now > nominationEnd && now < votingStart) {
        return { label: 'Nominations Closed', color: 'pending' };
      }
    }
    return { label: 'No Active Election', color: 'pending' };
  };
  
  const getBusinessUnitDisplayName = (businessUnit) => {
    const unitMap = {
      'unit_1': 'Business Unit 1',
      'unit_2': 'Business Unit 2',
      'unit_3': 'Business Unit 3',
      'unit_4': 'Business Unit 4',
      'unit_5': 'Business Unit 5'
    };
    return unitMap[businessUnit] || businessUnit;
  };
  
  const generateAvatarFromName = (name) => {
    if (!name) return '';
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return initials;
  };
  
  const getAvatarColor = (name) => {
    if (!name) return '#708090';
    const colors = ['#AB0E0E', '#009999', '#99D6D6', '#702963', '#D8EFEF'];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress color="primary" size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh',
        textAlign: 'center' 
      }}>
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          Error
        </Typography>
        <Typography variant="h6" sx={{ mb: 2, color: '#4a5568', fontWeight: 500 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  const currentStatus = getCurrentElectionStatus();
  const winnersArray = Object.values(displayedWinners || {}).filter(Boolean);

  return (
    <div className="dashboard-container">
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        <Typography 
          variant="h2" 
          component="h1" 
          className="dashboard-title"
        >
          Works Council Representatives
        </Typography>
        
        {/* Current Election Status - More Prominent */}
        {currentStatus.label === "No Active Election" ? (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 2,
            p: 1.25,
            borderRadius: '10px',
            background: 'rgba(239, 68, 68, 0.06)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            boxShadow: '0 1px 4px rgba(239, 68, 68, 0.06)'
          }}>
            <Typography variant="body1" sx={{
              color: '#dc2626',
              fontWeight: 600,
              textAlign: 'center',
              fontSize: '0.95rem'
            }}>
              ⚠️ No Active Election
            </Typography>
            <Typography variant="body2" sx={{
              color: '#4a5568',
              fontWeight: 500,
              textAlign: 'center',
              fontSize: '0.85rem'
            }}>
              There are currently no active elections.
            </Typography>
          </Box>
        ) : (
          <Box className="status-container" sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
            <StatusChip 
              status={currentStatus.color} 
              label={currentStatus.label} 
            />
            {currentElection && (
              <Typography variant="body1" sx={{ 
                fontWeight: 600,
                color: '#2d3748',
                mr: 2,
                fontSize: '1rem'
              }}>
                {currentElection.name}
              </Typography>
            )}
            {(currentElection || user.role === 'admin') && !(
              currentElection && (() => {
                const now = new Date();
                const nominationEndStr = currentElection.application_end_date;
                const votingStartStr = currentElection.voting_start_date;
                
                if (!nominationEndStr || !votingStartStr) {
                  console.log('Dashboard Button Debug: Missing date fields');
                  return false;
                }
                
                const nominationEnd = new Date(nominationEndStr + 'Z'); // Treat as UTC
                const votingStart = new Date(votingStartStr + 'Z'); // Treat as UTC
                
                // Check if dates are valid
                if (isNaN(nominationEnd.getTime()) || isNaN(votingStart.getTime())) {
                  console.log('Dashboard Button Debug: Invalid date values', {
                    nominationEndStr,
                    votingStartStr
                  });
                  return false;
                }
                
                console.log('Dashboard Button Debug:', {
                  now: now.toISOString(),
                  nominationEnd: nominationEnd.toISOString(),
                  votingStart: votingStart.toISOString(),
                  nowGreaterThanNominationEnd: now > nominationEnd,
                  nowLessThanVotingStart: now < votingStart,
                  shouldHideButton: now > nominationEnd && now < votingStart
                });
                
                return now > nominationEnd && now < votingStart;
              })()
            ) && (
              <Button 
                variant="contained" 
                color="primary"
                size="medium"
                onClick={handleElectionAction}
                sx={{
                  borderRadius: '10px',
                  px: 3,
                  py: 1,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #009999, #99D6D6)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #007777, #77C4C4)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 153, 153, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {(() => {
                  if (!currentElection) return user.role === 'admin' ? "Manage Elections" : "View";
                  const now = new Date();
                  if (now >= new Date(currentElection.application_start_date) && 
                      now <= new Date(currentElection.application_end_date)) {
                    return "Apply";
                  } else if (now >= new Date(currentElection.voting_start_date) && 
                            now <= new Date(currentElection.voting_end_date)) {
                    return "Vote";
                  } else {
                    return "View Election";
                  }
                })()} 
              </Button>
            )}
          </Box>
        )}
      </motion.div>
      
      {winnersArray.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <GlassCard>
            <Box className="empty-state">
              {/* Large Icon/Illustration */}
              <div className="empty-state-icon">
                🏛️
              </div>
              
              <Typography variant="h3" className="empty-state-title">
                No Representatives Yet
              </Typography>
              
              <Typography variant="h6" className="empty-state-description">
                Your Works Council representatives will be displayed here once the first election is completed. 
                Each business unit will have their elected representative showcased with their photo and details.
              </Typography>
              
              {/* Only show 'Start First Election' if NO election exists at all */}
              {!currentElection && !upcomingElection ? (
                user.role === 'admin' ? (
                  <Box sx={{ mt: 2 }}>
                    <Button 
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/admin')}
                      className="empty-state-button"
                    >
                      Start First Election
                    </Button>
                    <Typography variant="body2" sx={{ 
                      textAlign: 'center', 
                      fontSize: '0.9rem',
                      color: '#2d3748',
                      fontWeight: 500,
                      mb: 1
                    }}>Create and manage elections from the admin panel
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ 
                      color: '#718096',
                      fontWeight: 500
                    }}>
                      ⏳ Awaiting First Election
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      mt: 1, 
                      fontSize: '0.9rem',
                      color: '#718096'
                    }}>
                      Check back soon for application and voting opportunities
                    </Typography>
                  </Box>
                )
              ) : (
                /* If an election exists (voting or nomination), show appropriate message */
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ 
                    color: '#009999',
                    fontWeight: 500,
                    textAlign: 'center'
                  }}>
                    Election in Progress
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mt: 1, 
                    fontSize: '0.9rem',
                    color: '#2d3748',
                    textAlign: 'center'
                  }}>
                    Representatives will be displayed after the current election completes
                  </Typography>
                  {user.role === 'admin' && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Button 
                        variant="outlined"
                        size="medium"
                        onClick={() => navigate('/admin')}
                        sx={{
                          borderColor: '#009999',
                          color: '#009999',
                          '&:hover': {
                            borderColor: '#007777',
                            backgroundColor: 'rgba(0, 153, 153, 0.1)'
                          }
                        }}
                      >
                        Manage Current Election
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </GlassCard>
        </motion.div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
              <Grid container spacing={3}>
                {winnersArray
                  .sort((a, b) => (a.seat_number ?? 999) - (b.seat_number ?? 999))
                  .map((winner) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={2.4}
                      key={winner.seat_number}
                      component={motion.div}
                      variants={itemVariants}
                    >
                <GlassCard>
                  <Box sx={{ 
                    p: 2.5, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    minHeight: '300px',
                    position: 'relative',
                    opacity: winner.is_vacant ? 0.6 : 1
                  }}>
                    {winner.is_vacant ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          minHeight: '200px',
                          opacity: 0.4
                        }}
                      >
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          fontWeight="700" 
                          sx={{ 
                            mb: 0.5,
                            color: '#9ca3af',
                            fontSize: { xs: '1rem', md: '1.1rem' },
                            lineHeight: 1.3
                          }}
                        >
                          Position Vacant
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Business Unit Title - Smaller and more elegant */}
                        <Typography 
                          variant="subtitle2" 
                          component="h2" 
                          fontWeight="600" 
                          sx={{ 
                            mb: 2,
                            color: '#009999',
                            fontSize: { xs: '0.75rem', md: '0.85rem' },
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            opacity: 0.9
                          }}
                        >
                          {`Seat ${winner.seat_number}`}
                        </Typography>
                        
                        {/* Winner Avatar - Larger and more prominent */}
                        <Box
                          sx={{
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${getAvatarColor(winner.candidate_name)}, ${getAvatarColor(winner.candidate_name)}dd)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                            fontSize: '2.2rem',
                            fontWeight: 'bold',
                            color: 'white',
                            boxShadow: `0 6px 18px ${getAvatarColor(winner.candidate_name)}30`,
                            border: '3px solid rgba(255, 255, 255, 0.9)',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.2), transparent 60%)',
                              borderRadius: '50%'
                            }
                          }}
                        >
                          {winner.profile_picture ? (
                            <img
                              src={winner.profile_picture}
                              alt={winner.candidate_name}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '50%'
                              }}
                            />
                          ) : (
                            <Box sx={{ zIndex: 1 }}>
                              {generateAvatarFromName(winner.candidate_name)}
                            </Box>
                          )}
                        </Box>
                        
                        {/* Winner Name */}
                        <Box sx={{
                          position: 'relative',
                          mb: 1.5
                        }}>
                          <Typography 
                            variant="h6" 
                            component="h3" 
                            fontWeight="700" 
                            sx={{ 
                              mb: 0.5,
                              color: '#2d3748',
                              fontSize: { xs: '1.1rem', md: '1.25rem' },
                              lineHeight: 1.3
                            }}
                          >
                            {winner.candidate_name}
                          </Typography>
                        </Box>
                        
                        {/* Winner Title */}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 2, 
                            color: '#4a5568',
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            fontStyle: 'italic'
                          }}
                        >
                          Elected Representative
                        </Typography>
                        
                        <Divider sx={{ 
                          width: '80%', 
                          mb: 2,
                          borderColor: 'rgba(0, 153, 153, 0.2)'
                        }} />
                        
                        {/* Election Info with icon */}
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          mt: 'auto'
                        }}>
                          <Typography variant="body2" sx={{ 
                            textAlign: 'center',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#602473',
                            letterSpacing: '0.2px'
                          }}>
                            {winner.business_unit || "erorr"}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </motion.div>
       )}
     </div>
   );
};

export default Dashboard;
